import {
  lazy, Suspense, memo, LazyExoticComponent, Fragment,
} from 'react';
import { useSelector } from 'react-redux';

import { EVENTS } from '../../constants/postMessage';
import {
  DAILY_LIMIT_EXCEEDED_DIALOG,
  DISCONNECTED_FOR_INACTIVITY_DIALOG,
  DISCONNECTED_SESSION_DIALOG,
  DISCONNECTION_DIALOG,
  LOW_BALANCE_DIALOG,
  LOW_REAL_BALANCE_DIALOG,
  NO_VIDEO_DIALOG,
  TABLE_CLOSED_DIALOG,
  VIDEO_ERROR_DIALOG,
  RECONNECTING_SOCKET_DIALOG,
  REALITY_CHECK_DIALOG,
  UKGC_DIALOG,
  OPERATOR_ERROR_DIALOG,
  GENERIC_DIALOG,
  REGULATIONS_DIALOG,
  DISCONNECTED_FROM_OPERATOR_DIALOG,
  REGULATIONS_OSS_DIALOG,
  GEOLOCATION_ERROR_DIALOG,
  RECONNECTING_SCREEN_DIALOG,
  PLAYER_NR_TABLE_LIMIT_REACHED_DIALOG,
  WELCOME_DIALOG,
  BONUS_WHEEL_INSUFFICIENT_BALANCE_DIALOG,
} from '../../store/constants/dialog';
import {
  dialogNameSelector,
  gameSelector,
  isEvoWrappedSelector,
  localeSelector,
  widgetsSelector,
} from '../../store/selectors';
import { logDevelopmentError } from '../../util/logDevelopmentError';
import { postMessage } from '../../util/postMessage';
import Reconnecting from './dialogs/Reconnecting';
import ReconnectingScreen from './dialogs/ReconnectingScreen/ReconnectingScreen';
import {
  DIALOGS_DISPLAYED_VIA_OSS_WRAPPER,
  DIALOGS_NOT_RENDERED_IN_OSS,
  OSS_DIALOGS_DATA_MAP,
} from './oss/constants';
import { getOssDialogLocalizedData } from './oss/utils';

// cannot have dynamic loading on disconnection
const disconnectionDialogPromise = import('./dialogs/Disconnection');

const LowBalance = lazy(() => import('./dialogs/LowBalance'));
const Disconnection = lazy(() => disconnectionDialogPromise);
const DisconnectedSession = lazy(() => import('./dialogs/DisconnectedSession'));
const DisconnectedForInactivity = lazy(
  () => import('./dialogs/DisconnectedForInactivity')
);
const TableClosed = lazy(() => import('./dialogs/TableClosed'));
const NoVideo = lazy(() => import('./dialogs/NoVideo'));
const VideoError = lazy(() => import('./dialogs/VideoError'));
const DailyLimitExceeded = lazy(() => import('./dialogs/DailyLimitExceeded'));
const LowRealBalance = lazy(() => import('./dialogs/LowRealBalance'));
const RealityCheck = lazy(() => import('./dialogs/RealityCheck'));
const UKGCMessage = lazy(() => import('./dialogs/UKGCMessage'));
const OperatorError = lazy(() => import('./dialogs/OperatorError'));
const Generic = lazy(() => import('./dialogs/Generic'));
const Regulations = lazy(() => import('./dialogs/Regulations'));
const RegulationsOSS = lazy(() => import('./oss/dialogs/Regulations'));
const DisconnectedFromOperator = lazy(
  () => import('./dialogs/DisconnectedFromOperator')
);
const GeolocationError = lazy(() => import('./dialogs/GeolocationError'));
const PlayerNrTableLimitReached = lazy(() => import('./dialogs/PlayerNrTableLimitReached.js'));
const BonusWheelInsufficientBalanceDialog = lazy(
  () => import('./dialogs/BonusWheelInsufficientBalanceDialog/BonusWheelInsufficientBalanceDialog')
);

type GameDialogProps = {
  gameDialogs?: Record<string, LazyExoticComponent<any>>;
};

const GameDialog = ({ gameDialogs = {} }: GameDialogProps) => {
  const DIALOGS_MAP = {
    [LOW_BALANCE_DIALOG]: LowBalance,
    [DISCONNECTION_DIALOG]: Disconnection,
    [DISCONNECTED_SESSION_DIALOG]: DisconnectedSession,
    [DISCONNECTED_FOR_INACTIVITY_DIALOG]: DisconnectedForInactivity,
    [TABLE_CLOSED_DIALOG]: TableClosed,
    [NO_VIDEO_DIALOG]: NoVideo,
    [VIDEO_ERROR_DIALOG]: VideoError,
    [DAILY_LIMIT_EXCEEDED_DIALOG]: DailyLimitExceeded,
    [LOW_REAL_BALANCE_DIALOG]: LowRealBalance,
    [RECONNECTING_SOCKET_DIALOG]: Reconnecting,
    [REALITY_CHECK_DIALOG]: RealityCheck,
    [UKGC_DIALOG]: UKGCMessage,
    [OPERATOR_ERROR_DIALOG]: OperatorError,
    [GENERIC_DIALOG]: Generic,
    [REGULATIONS_DIALOG]: Regulations,
    [REGULATIONS_OSS_DIALOG]: RegulationsOSS,
    [DISCONNECTED_FROM_OPERATOR_DIALOG]: DisconnectedFromOperator,
    [GEOLOCATION_ERROR_DIALOG]: GeolocationError,
    [RECONNECTING_SCREEN_DIALOG]: ReconnectingScreen,
    [PLAYER_NR_TABLE_LIMIT_REACHED_DIALOG]: PlayerNrTableLimitReached,
    [WELCOME_DIALOG]: Fragment,
    [BONUS_WHEEL_INSUFFICIENT_BALANCE_DIALOG]: BonusWheelInsufficientBalanceDialog,
    ...gameDialogs,
  };

  const name = useSelector(dialogNameSelector) as keyof typeof DIALOGS_MAP;
  const isEvoWrapped = useSelector(isEvoWrappedSelector);
  const locale = useSelector(localeSelector);
  const game = useSelector(gameSelector);
  const widgets = useSelector(widgetsSelector);

  if (!name) return null;

  if (name === WELCOME_DIALOG && !game.started) {
    return null;
  }

  const Dialog = DIALOGS_MAP[name];
  if (!Dialog) return null;
  if (isEvoWrapped && DIALOGS_NOT_RENDERED_IN_OSS.includes(name)) return null;

  if (isEvoWrapped && DIALOGS_DISPLAYED_VIA_OSS_WRAPPER.includes(name)) {
    try {
      postMessage(
        {
          event: EVENTS.DIALOG_ADD,
          id: `Ezugi${name}`,
          ...getOssDialogLocalizedData(
            OSS_DIALOGS_DATA_MAP[name as keyof typeof OSS_DIALOGS_DATA_MAP],
            locale,
            widgets
          ),
        },
        false
      );
    } catch (error: any) {
      logDevelopmentError(error.message);
    }

    return null;
  }

  return (
    <Suspense fallback={null}>
      <Dialog
        on={true}
        style={{}}
        className=""
        onClose={() => {}}
        buttonList={[]}
      />
    </Suspense>
  );
};

export default memo(GameDialog);
